import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { selectCurrentSpeed, selectIsDrivingMode } from '../mapSlice';

/**
 * Automatically adjusts map zoom level based on current vehicle speed.
 * 
 * Logic:
 * - Speed < 30 km/h: Zoom 18 (Detail view)
 * - Speed 30-50 km/h: Zoom 17
 * - Speed 50-80 km/h: Zoom 16
 * - Speed > 80 km/h: Zoom 15 (Highway view)
 * 
 * Includes hysteresis to prevent rapid zooming when hovering around thresholds.
 */
export const useDynamicZoom = () => {
    const map = useMap();
    const currentSpeed = useSelector(selectCurrentSpeed); // in km/h
    const isDrivingMode = useSelector(selectIsDrivingMode);

    // settings for zoom levels
    const ZOOM_Levels = {
        SLOW: 18,   // < 30
        CITY: 17,   // 30 - 50
        FAST: 16,   // 50 - 80
        HIGHWAY: 15 // > 80
    };

    const lastZoomRef = useRef(null);
    const transitionTimeoutRef = useRef(null);

    useEffect(() => {
        if (!isDrivingMode) return;

        // Determine target zoom based on speed
        let targetZoom = ZOOM_Levels.CITY; // Default

        if (currentSpeed < 30) {
            targetZoom = ZOOM_Levels.SLOW;
        } else if (currentSpeed < 50) {
            targetZoom = ZOOM_Levels.CITY;
        } else if (currentSpeed < 80) {
            targetZoom = ZOOM_Levels.FAST;
        } else {
            targetZoom = ZOOM_Levels.HIGHWAY;
        }

        // Check if we need to change zoom
        const currentZoom = map.getZoom();

        // Optimization: Don't trigger if already at target or very close (user might have manual zoomed? 
        // For now, we enforce auto-zoom. If user manual zooms, it will snap back on next speed update.
        // Todo: Add manual override detection?)

        if (targetZoom !== currentZoom && targetZoom !== lastZoomRef.current) {
            // Basic Hysteresis: Only change if the new target persists? 
            // Or just change immediately but smooth animation handles it.
            // Let's add a small check to avoid jitter at exact 30.0km/h etc.

            // Changing zoom is jarring. Let's do it gently.
            // Leaflet flyTo or setZoom with animate.

            // Cancel pending
            if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);

            // Wait a tiny bit to ensure speed is stable? 
            // actually speed updates are every 1s usually.

            map.setZoom(targetZoom, { animate: true, duration: 1.5 });
            lastZoomRef.current = targetZoom;
        }

    }, [currentSpeed, isDrivingMode, map]);

    // Reset when exiting driving mode? 
    // Maybe keep last zoom or reset to default? 
    // Standard behavior: Keep where it is.
};
